import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Typography,
  Divider,
  Paper,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Box,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { stcAPI, employeesAPI } from '../../../services/api';
import {
  STC, STCGains, STCEmployeeInfo, STCPrefill, STCPrimeLine,
  STC_GAIN_ROWS, STC_LEGACY_PRIME_KEYS, STC_JOURS_MOIS,
} from '../../../types/stc';
import { MotifSortie, MOTIF_SORTIE_OPTIONS } from '../../../types/employee';
import { extractErrorMessage } from '../../../utils/errorHandler';
import useAlert from '../../../hooks/useAlert';
import PriceInput, { parsePriceValue } from '../../../components/PriceInput';

const HR_COLOR = '#00897B';
const CNSS_RATE = 0.0448;
const AMO_RATE = 0.0226;
const CNSS_CEILING = 6000;

const GAIN_KEYS: (keyof STCGains)[] = [
  ...STC_GAIN_ROWS.map((r) => r.key),
  'cnss_base',
  'amo_base',
];

// Form values are number | string | null (PriceInput emits a string while typing).
type GainsForm = Record<keyof STCGains, number | string | null>;

// A dynamic prime line while being edited (montant may be a partial string).
type LineForm = Omit<STCPrimeLine, 'montant'> & { montant: number | string | null };

const LEGACY_KEYS = new Set<keyof STCGains>(STC_LEGACY_PRIME_KEYS);

// All gain/base fields default to 0 (shown as "0", not a blank cell).
const zeroGains = (): GainsForm =>
  GAIN_KEYS.reduce((acc, k) => { acc[k] = 0; return acc; }, {} as GainsForm);

const MONTHLY_HOURS = 191.33; // 44h/week legal base, for the hourly rate

/** Number | null, keeping null/undefined/"" distinct from a real 0. */
const numOrNull = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** Worked days read as "16" when whole, "15,5" otherwise. */
const fmtJours = (j: number): string =>
  Number.isInteger(j) ? String(j) : String(round2(j)).replace('.', ',');

/** Seniority rate 0.05 → "5". */
const fmtRate = (taux: number): string => String(round2(taux * 100)).replace('.', ',');

/** Equal to the cent — both sides are already rounded to 2 decimals. */
const sameAmount = (a: number, b: number): boolean => Math.abs(a - b) < 0.005;

const seniorityYears = (embauche?: string | null, sortie?: string | null): number => {
  if (!embauche || !sortie) return 0;
  const d1 = new Date(embauche).getTime();
  const d2 = new Date(sortie).getTime();
  if (isNaN(d1) || isNaN(d2) || d2 <= d1) return 0;
  return (d2 - d1) / (1000 * 60 * 60 * 24 * 365.25);
};

// Indemnité de licenciement hours per year of seniority (Code du Travail art. 53).
const licenciementHours = (years: number): number => {
  let h = 0;
  h += Math.min(years, 5) * 96;
  if (years > 5) h += Math.min(years - 5, 5) * 144;
  if (years > 10) h += Math.min(years - 10, 5) * 192;
  if (years > 15) h += (years - 15) * 240;
  return h;
};

const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Suggested values for a NEW STC, from reliable inputs only (salaire_base,
 * date d'embauche, motif). Every other gain field defaults to 0. All editable.
 *
 * `facteur` prorates the exit month on pointage hours (01 → date de sortie),
 * the same formula as Base de Calcul Paie. Indemnité de licenciement is NOT
 * prorated — it is due on the full salary regardless of the last month worked.
 */
const prefillGains = (emp: STCEmployeeInfo, motif: string, facteur = 1, exitDate?: string | null): GainsForm => {
  const g = zeroGains();
  const base = Number(emp.salaire_base) || 0;
  if (base <= 0) return g;

  const proratedBase = round2(base * facteur);
  // Seniority (for licenciement) runs to the chosen exit date, not the stored one.
  const years = seniorityYears(emp.date_embauche, exitDate || emp.date_sortie);
  const lic = motif === 'LICENCIEMENT' && years > 0
    ? round2(licenciementHours(years) * (base / MONTHLY_HOURS))
    : 0;

  g.salaire_base = proratedBase;
  // Prime d'ancienneté is computed by the backend prefill (prorated salary × taux)
  // and injected in buildPrefillGains — not here.
  if (lic > 0) g.indemnite_licenciement = lic;
  g.cnss_base = proratedBase;
  g.amo_base = proratedBase;
  return g;
};

/**
 * Gain suggestions for a NEW STC = prefillGains + the backend-computed
 * prime d'ancienneté (0 when the prefill is unavailable).
 */
const buildPrefillGains = (
  emp: STCEmployeeInfo, motif: string, pf: STCPrefill | null, exitDate?: string | null,
): GainsForm => {
  const g = prefillGains(emp, motif, pf ? Number(pf.facteur) : 1, exitDate);
  if (pf) {
    // Take the backend's prorated salary as-is instead of recomputing it here:
    // `pf.facteur` is rounded to 4 dp for display and lands a few centimes off
    // (3 423 × 0,4615 = 1 579,71 against a true 3 423 × 12/26 = 1 579,85).
    // prime_anciennete is already computed from the exact figure, so recomputing
    // here also left the two inconsistent.
    const prorated = Number(pf.salaire_base_prorate) || 0;
    g.salaire_base = prorated;
    g.cnss_base = prorated;
    g.amo_base = prorated;
  }
  g.prime_anciennete = pf ? (Number(pf.prime_anciennete) || 0) : 0;
  return g;
};

const fmt = (n: number) =>
  n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface Props {
  open: boolean;
  employee: STCEmployeeInfo;
  onClose: () => void;
  onSaved: () => void;
  onPrint: (stcId: number) => void;
}

const STCFormDialog: React.FC<Props> = ({ open, employee, onClose, onSaved, onPrint }) => {
  const { showSuccess, showError } = useAlert();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [existing, setExisting] = useState<STC | null>(null);
  const [dateEtab, setDateEtab] = useState(new Date().toISOString().split('T')[0]);
  const [motif, setMotif] = useState<MotifSortie | ''>('');
  const [dateSortie, setDateSortie] = useState<string>(''); // exit date, drives proration
  const [showErrors, setShowErrors] = useState(false);      // highlight required fields after a failed save
  const [gains, setGains] = useState<GainsForm>(zeroGains());
  const [lines, setLines] = useState<LineForm[]>([]);
  const [prefill, setPrefill] = useState<STCPrefill | null>(null);
  /**
   * Proration fetched for a SAVED receipt that carries no audit trail (issued
   * before it existed). Used only to explain the frozen amounts — it never
   * rewrites them, which is why it is kept apart from `prefill`.
   */
  const [trailFallback, setTrailFallback] = useState<STCPrefill | null>(null);

  const load = useCallback(async () => {
    if (!open || !employee) return;
    setLoading(true);
    setShowErrors(false);
    setMotif((employee.motif_sortie as MotifSortie) || '');
    setDateSortie((employee.date_sortie || '').split('T')[0]);
    try {
      const stc = await stcAPI.getByEmployee(employee.id);
      setExisting(stc);
      setPrefill(null); // saved record: show the frozen values, never re-prorate
      setTrailFallback(null);
      // Receipts issued before the audit trail existed have no formula to
      // replay. Fetch the proration for their exit date so the calculation is
      // still visible; it explains the frozen amounts without changing them,
      // and gets frozen onto the record the next time HR saves.
      if (numOrNull(stc.proration_jours) === null) {
        try {
          setTrailFallback(await stcAPI.getPrefill(
            employee.id, (employee.date_sortie || '').split('T')[0] || undefined,
          ));
        } catch {
          // No pointage for that period — no formula, exactly as before.
        }
      }
      setDateEtab(stc.date_etablissement);
      const g = zeroGains();
      GAIN_KEYS.forEach((k) => {
        g[k] = Number(stc[k]) || 0; // show every field's value, 0 included (never blank)
      });
      setGains(g);
      setLines((stc.primes_lines || []).map((l) => ({ ...l, montant: Number(l.montant) || 0 })));
    } catch {
      // No STC yet — prorate the exit month on pointage and seed one line per
      // prime the employee actually has. All suggestions remain editable.
      setExisting(null);
      setTrailFallback(null);
      setDateEtab(new Date().toISOString().split('T')[0]);
      let pf: STCPrefill | null = null;
      try {
        pf = await stcAPI.getPrefill(employee.id);
      } catch {
        pf = null; // prefill unavailable — fall back to the un-prorated suggestions
      }
      setPrefill(pf);
      setGains(buildPrefillGains(employee, (employee.motif_sortie as string) || '', pf, pf?.date_fin));
      setLines(pf ? pf.primes_lines.map((l) => ({ ...l, montant: Number(l.montant) || 0 })) : []);
    } finally {
      setLoading(false);
    }
  }, [open, employee]);

  useEffect(() => { load(); }, [load]);

  // Recompute the proration when the exit date changes (re-derives gains + prime lines).
  const recompute = useCallback(async (exitDate: string) => {
    if (!employee) return;
    setLoading(true);
    try {
      const pf = await stcAPI.getPrefill(employee.id, exitDate || undefined);
      setPrefill(pf);
      setGains(buildPrefillGains(employee, motif || '', pf, exitDate || pf?.date_fin));
      setLines(pf ? pf.primes_lines.map((l) => ({ ...l, montant: Number(l.montant) || 0 })) : []);
    } catch {
      setPrefill(null);
    } finally {
      setLoading(false);
    }
  }, [employee, motif]);

  const setField = (key: keyof STCGains, value: number | string | null) => {
    setGains((prev) => ({ ...prev, [key]: value }));
  };

  const setLine = (index: number, patch: Partial<LineForm>) => {
    setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  };

  const addLine = () => {
    setLines((prev) => [...prev, { libelle: '', montant: '', type: 'manuel', prorated: false }]);
  };

  const removeLine = (index: number) => {
    setLines((prev) => prev.filter((_, i) => i !== index));
  };

  // Legacy fixed prime columns: only rendered when an older record has a value.
  const visibleGainRows = useMemo(
    () => STC_GAIN_ROWS.filter(
      (r) => !LEGACY_KEYS.has(r.key) || parsePriceValue(gains[r.key]) > 0
    ),
    [gains],
  );

  const totals = useMemo(() => {
    const fixedSum = STC_GAIN_ROWS.reduce((s, r) => s + parsePriceValue(gains[r.key]), 0);
    const linesSum = lines.reduce((s, l) => s + parsePriceValue(l.montant), 0);
    const gainSum = fixedSum + linesSum;
    const cnss = Math.min(parsePriceValue(gains.cnss_base), CNSS_CEILING) * CNSS_RATE;
    const amo = parsePriceValue(gains.amo_base) * AMO_RATE;
    // auto: Prêts + Acomptes (backend). Saved STC → its frozen derive value;
    // a new STC → the live prefill so the deduction shows before the first save.
    const acompte = existing
      ? Number(existing.acompte_salaire) || 0
      : (prefill ? Number(prefill.acompte_salaire) || 0 : 0);
    const totalRet = cnss + amo + acompte;
    return { gainSum, linesSum, cnss, amo, acompte, totalRet, net: gainSum - totalRet };
  }, [gains, lines, existing, prefill]);

  /**
   * How the amounts on screen were computed — the live prefill for a new STC,
   * the frozen columns for a saved one. Null when neither is available (receipts
   * issued before the audit trail existed), in which case no formula is shown.
   */
  const proration = useMemo(() => {
    const fromPrefill = (pf: STCPrefill) => ({
      jours: pf.jours_travailles,
      facteur: pf.facteur,
      salaireComplet: pf.salaire_base_complet,
      tauxAnc: pf.taux_anciennete,
    });
    // Live prefill (new STC) → the record's own frozen trail → a fallback fetched
    // for a receipt issued before the trail existed.
    const stored = {
      jours: existing?.proration_jours,
      facteur: existing?.proration_facteur,
      salaireComplet: existing?.salaire_base_complet,
      tauxAnc: existing?.taux_anciennete,
    };
    const src = prefill
      ? fromPrefill(prefill)
      : numOrNull(stored.jours) !== null
        ? stored
        : trailFallback
          ? fromPrefill(trailFallback)
          : stored;
    const jours = numOrNull(src.jours);
    const facteur = numOrNull(src.facteur);
    if (jours === null || facteur === null) return null;
    return {
      jours,
      facteur,                                            // rounded to 4 dp — display only
      // jours/26 IS the factor the backend prorates with (it rounds the day
      // count first so the printed equation holds). The `facteur` field is a
      // 4 dp display rounding of that same fraction — using it here would print
      // 6 650 × 0,6154 = 4 092,41 against a stored 4 092,31 and mark every
      // untouched row "(modifié)".
      facteurExact: Math.min(jours / STC_JOURS_MOIS, 1),
      salaireComplet: numOrNull(src.salaireComplet),
      tauxAnc: numOrNull(src.tauxAnc),
    };
  }, [prefill, existing, trailFallback]);

  /**
   * "6 650,00 × 16/26 j = 4 092,31". `(modifié)` is appended when the field no
   * longer holds the formula's result, so the line never contradicts the input
   * right above it after HR overrides an amount by hand.
   */
  const prorataFormula = (base: number | null, current: number): string | null => {
    if (!proration || base === null || base <= 0) return null;
    const result = round2(base * proration.facteurExact);
    const txt = `${fmt(base)} × ${fmtJours(proration.jours)}/${STC_JOURS_MOIS} j = ${fmt(result)}`;
    return sameAmount(result, current) ? txt : `${txt} (modifié)`;
  };

  /** Formula under a fixed gain input — only the two computed rubriques have one. */
  const gainFormula = (key: keyof STCGains): string | null => {
    if (!proration) return null;
    const current = parsePriceValue(gains[key]);
    if (key === 'salaire_base') return prorataFormula(proration.salaireComplet, current);
    if (key === 'prime_anciennete') {
      const taux = proration.tauxAnc;
      if (taux === null || taux <= 0) return null;
      // Base is the salary currently in the form, so editing it keeps this honest.
      const base = parsePriceValue(gains.salaire_base);
      const result = round2(base * taux);
      const txt = `${fmt(base)} × ${fmtRate(taux)} % = ${fmt(result)}`;
      return sameAmount(result, current) ? txt : `${txt} (modifié)`;
    }
    return null;
  };

  /**
   * The un-prorated amount a line came from: its own frozen base, or — for a
   * line saved before that was recorded — the matching prime in the fallback.
   */
  const lineBase = (line: LineForm): number | null => {
    const own = numOrNull(line.montant_base);
    if (own !== null) return own;
    if (!trailFallback) return null;
    const label = (line.libelle || '').trim();
    const match = trailFallback.primes_lines.find((l) => (
      line.prime_id ? l.prime_id === line.prime_id : l.libelle.trim() === label
    ));
    return match ? numOrNull(match.montant_base) : null;
  };

  /** Formula under a dynamic prime line, from the base frozen on the line. */
  const lineFormula = (line: LineForm): string | null => {
    const base = lineBase(line);
    if (base === null || base <= 0) return null;
    const current = parsePriceValue(line.montant);
    if (line.prorated === false) {
      const txt = `Montant plein ${fmt(base)} — non proratée`;
      return sameAmount(base, current) ? txt : `${txt} (modifié)`;
    }
    return prorataFormula(base, current);
  };

  const handleSave = async (thenPrint: boolean) => {
    // Required: date de sortie, motif de départ, date d'établissement.
    if (!dateSortie || !motif || !dateEtab) {
      setShowErrors(true);
      showError('Renseignez la date de sortie, le motif de départ et la date d\'établissement.');
      return;
    }
    // A rubrique printed on the receipt must be named.
    if (lines.some((l) => (l.libelle || '').trim() === '' && parsePriceValue(l.montant) > 0)) {
      showError('Chaque ligne de prime avec un montant doit avoir un libellé.');
      return;
    }
    setSaving(true);
    try {
      const payload = GAIN_KEYS.reduce((acc, k) => {
        acc[k] = parsePriceValue(gains[k]);
        return acc;
      }, {} as Record<keyof STCGains, number>);

      // Freeze the prime lines onto the receipt (drop blank rows).
      const primes_lines: STCPrimeLine[] = lines
        .filter((l) => (l.libelle || '').trim() !== '' || parsePriceValue(l.montant) > 0)
        .map((l) => ({
          libelle: (l.libelle || '').trim(),
          montant: parsePriceValue(l.montant),
          montant_base: lineBase(l),   // resolved, so re-saving backfills older lines
          prime_id: l.prime_id ?? null,
          type: l.type || 'manuel',
          prorated: l.prorated ?? false,
        }));

      // Freeze how the amounts were computed, so reopening the receipt replays
      // the same arithmetic instead of re-deriving it from pointage that may
      // have moved since. Omitted entirely when there is nothing to record.
      const prorationPatch = proration
        ? {
          proration_jours: proration.jours,
          proration_facteur: proration.facteur,
          ...(proration.salaireComplet !== null && { salaire_base_complet: proration.salaireComplet }),
          ...(proration.tauxAnc !== null && { taux_anciennete: proration.tauxAnc }),
        }
        : {};

      // Motif + date de sortie live on the employee — persist if changed.
      const empPatch: Record<string, any> = {};
      if (motif !== ((employee.motif_sortie as MotifSortie) || '')) {
        empPatch.motif_sortie = (motif || null);
      }
      if (dateSortie !== (employee.date_sortie || '').split('T')[0]) {
        empPatch.date_sortie = dateSortie || null;
      }
      if (Object.keys(empPatch).length) {
        await employeesAPI.update(employee.id, empPatch);
      }

      let saved: STC;
      if (existing) {
        saved = await stcAPI.update(existing.id, {
          date_etablissement: dateEtab, ...payload, primes_lines, ...prorationPatch,
        });
      } else {
        saved = await stcAPI.create({
          employee_id: employee.id, date_etablissement: dateEtab, ...payload, primes_lines, ...prorationPatch,
        });
      }
      setExisting(saved);
      showSuccess('STC enregistré');
      onSaved();
      onClose();
      if (thenPrint) onPrint(saved.id);
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ bgcolor: HR_COLOR, color: 'white' }}>
        Solde de Tout Compte — {employee?.nom_complet}
        {existing?.ref_stc && (
          <Chip label={existing.ref_stc} size="small" sx={{ ml: 1, bgcolor: 'white', color: HR_COLOR, fontWeight: 'bold' }} />
        )}
      </DialogTitle>
      <DialogContent dividers>
        {/* Employee identity (read-only) */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#E0F2F1' }}>
          <Grid container spacing={1}>
            <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Matricule</Typography><Typography variant="body2" fontWeight={600}>{employee?.matricule}</Typography></Grid>
            <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">CIN</Typography><Typography variant="body2" fontWeight={600}>{employee?.cin || '-'}</Typography></Grid>
            <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">N° CNSS</Typography><Typography variant="body2" fontWeight={600}>{employee?.numero_cnss || '-'}</Typography></Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                required
                label="Date de sortie"
                type="date"
                size="small"
                value={dateSortie}
                error={showErrors && !dateSortie}
                onChange={(e) => {
                  const v = e.target.value;
                  setDateSortie(v);
                  // Recompute proration on a valid (or cleared) date.
                  if (v === '' || !isNaN(new Date(v).getTime())) recompute(v);
                }}
                InputLabelProps={{ shrink: true }}
                helperText={showErrors && !dateSortie ? 'Obligatoire' : 'Prorata 01 → cette date'}
                fullWidth
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControl size="small" fullWidth required error={showErrors && !motif}>
                <InputLabel>Motif de départ</InputLabel>
                <Select
                  label="Motif de départ"
                  value={motif}
                  onChange={(e) => setMotif((e.target.value || '') as MotifSortie | '')}
                >
                  <MenuItem value=""><em>Non renseigné</em></MenuItem>
                  {MOTIF_SORTIE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
                {showErrors && !motif && <FormHelperText>Obligatoire</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                required
                label="Date d'établissement"
                type="date"
                size="small"
                value={dateEtab}
                error={showErrors && !dateEtab}
                onChange={(e) => setDateEtab(e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText={showErrors && !dateEtab ? 'Obligatoire' : ' '}
                fullWidth
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Proration of the exit month (new STC only) */}
        {prefill && (
          <Alert
            severity={prefill.has_pointage ? 'info' : 'warning'}
            sx={{ mb: 2 }}
          >
            {prefill.has_pointage ? (
              <>
                Mois de sortie prorata&nbsp;
                <b>{new Date(prefill.date_debut).toLocaleDateString('fr-FR')}</b> →{' '}
                <b>{new Date(prefill.date_fin).toLocaleDateString('fr-FR')}</b> :
                {' '}(HP={fmt(Number(prefill.heures_presence))}h + JC={prefill.jours_conge}j
                {' '}+ JF={prefill.jours_ferie}j) = {fmt(Number(prefill.heures_reelles))}h / 191 ={' '}
                <b>{(Number(prefill.facteur) * 100).toFixed(1)}%</b>
                {' → '}
                <b>{fmtJours(Number(prefill.jours_travailles))} / {STC_JOURS_MOIS} jours travaillés</b>
                {' — '}salaire de base {fmt(Number(prefill.salaire_base_complet))} →{' '}
                <b>{fmt(Number(prefill.salaire_base_prorate))} MAD</b>. Valeurs modifiables.
              </>
            ) : (
              <>
                Aucun pointage saisi pour la période{' '}
                {new Date(prefill.date_debut).toLocaleDateString('fr-FR')} →{' '}
                {new Date(prefill.date_fin).toLocaleDateString('fr-FR')} : le prorata n'a pas pu
                être calculé, les montants sont proposés au mois complet (100%). Corrigez-les
                manuellement ou saisissez le pointage.
              </>
            )}
          </Alert>
        )}

        {/* GAINS (manual entry) */}
        <Typography variant="subtitle2" sx={{ color: HR_COLOR, mb: 1, fontWeight: 700 }}>Gains (saisie manuelle)</Typography>
        <Grid container spacing={2}>
          {visibleGainRows.map((row) => (
            <Grid item xs={12} sm={6} key={row.key}>
              <PriceInput
                label={row.label}
                variant="outlined"
                value={gains[row.key]}
                onChange={(v) => setField(row.key, v)}
                helperText={gainFormula(row.key) || ' '}
              />
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* PRIMES — dynamic lines, one per prime the employee actually has */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ color: HR_COLOR, fontWeight: 700 }}>
            Primes &amp; indemnités
            {lines.length > 0 && (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                ({lines.length} ligne{lines.length > 1 ? 's' : ''} — total {fmt(totals.linesSum)} MAD)
              </Typography>
            )}
          </Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={addLine} sx={{ color: HR_COLOR }}>
            Ajouter une ligne
          </Button>
        </Box>

        {lines.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Aucune prime pour cet employé. Cliquez sur « Ajouter une ligne » pour en saisir une.
          </Typography>
        ) : (
          <Grid container spacing={1}>
            {lines.map((line, index) => (
              <Grid item xs={12} key={index}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <TextField
                    label="Libellé"
                    size="small"
                    fullWidth
                    value={line.libelle}
                    onChange={(e) => setLine(index, { libelle: e.target.value })}
                  />
                  <Box sx={{ width: 220, flexShrink: 0 }}>
                    <PriceInput
                      label="Montant"
                      variant="outlined"
                      value={line.montant}
                      onChange={(v) => setLine(index, { montant: v })}
                      helperText={
                        lineFormula(line)
                        || (line.prime_id
                          ? (line.prorated
                            ? `Prorata appliqué${proration ? ` (${(proration.facteur * 100).toFixed(1)}%)` : ''}`
                            : 'Montant plein (prime non proratée)')
                          : ' ')
                      }
                    />
                  </Box>
                  <Tooltip title="Supprimer la ligne">
                    <IconButton onClick={() => removeLine(index)} sx={{ mt: 0.5 }}>
                      <DeleteIcon color="error" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}

        <Divider sx={{ my: 2 }} />

        {/* RETENUES */}
        <Typography variant="subtitle2" sx={{ color: HR_COLOR, mb: 1, fontWeight: 700 }}>Retenues</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <PriceInput
              label="CNSS — base"
              variant="outlined"
              value={gains.cnss_base}
              onChange={(v) => setField('cnss_base', v)}
              helperText="4,48% (plafond 6000)"
            />
          </Grid>
          <Grid item xs={6} sm={2}><Typography variant="body2">= <b>{fmt(totals.cnss)}</b></Typography></Grid>
          <Grid item xs={12} sm={4}>
            <PriceInput
              label="AMO — base"
              variant="outlined"
              value={gains.amo_base}
              onChange={(v) => setField('amo_base', v)}
              helperText="2,26%"
            />
          </Grid>
          <Grid item xs={6} sm={2}><Typography variant="body2">= <b>{fmt(totals.amo)}</b></Typography></Grid>
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              Acompte / Prêt sur salaire (auto) : <b>{fmt(totals.acompte)} MAD</b>
            </Typography>
          </Grid>
        </Grid>

        {/* Summary */}
        <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: '#F1F8E9' }}>
          <Grid container spacing={1}>
            <Grid item xs={4}><Typography variant="caption" color="text.secondary">Total brut</Typography><Typography variant="h6">{fmt(totals.gainSum)}</Typography></Grid>
            <Grid item xs={4}><Typography variant="caption" color="text.secondary">Total retenues</Typography><Typography variant="h6">{fmt(totals.totalRet)}</Typography></Grid>
            <Grid item xs={4}><Typography variant="caption" color="text.secondary">Net à payer</Typography><Typography variant="h6" sx={{ color: HR_COLOR, fontWeight: 800 }}>{fmt(totals.net)}</Typography></Grid>
          </Grid>
        </Paper>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="inherit">Fermer</Button>
        <Button onClick={() => handleSave(false)} variant="outlined" disabled={saving || loading} sx={{ color: HR_COLOR, borderColor: HR_COLOR }}>
          Enregistrer
        </Button>
        <Button onClick={() => handleSave(true)} variant="contained" startIcon={<PdfIcon />} disabled={saving || loading} sx={{ bgcolor: HR_COLOR, '&:hover': { bgcolor: '#00695C' } }}>
          Enregistrer & Imprimer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default STCFormDialog;
